/**
 * Application_Form — phase-gating pure logic
 * (application-form-builder spec, task 9.1).
 *
 * This module holds the I/O-free heart of Requirement 16: each Form_Page is
 * assigned a `Phase` (`PHASE_1` / `PHASE_2`); Phase_2 pages become available to
 * an Applicant only once the application reaches `APPROVED`. The phase gate is
 * deliberately a one-line total predicate so it is *identical* on the renderer
 * and the server — the same precedent the other pure modules follow
 * (`division-registry/visibility.ts`, `person-service/personLogic.ts`,
 * `titlePreferences.ts`). There is no AWS SDK and no I/O here — only the rules.
 *
 * What lives here:
 *   - `isPhaseTwoUnlocked(applicationStatus)` — the phase gate, which is exactly
 *     `applicationStatus === APPROVED` (Req 16.4). Page-approval is a separate,
 *     independent axis (Req 16.5), so this predicate reads only
 *     `applicationStatus`.
 *   - `availablePages(pages, applicationStatus)` — all Phase_1 pages, plus
 *     Phase_2 pages iff Phase_2 is unlocked (Req 16.2, 16.3). It preserves the
 *     incoming page order and composes with the per-division and audience
 *     visibility checks (`formStructure.ts` / `audienceVisibility.ts`) at the
 *     call site: a page must be phase-available AND effectively visible to be
 *     rendered.
 *
 * Validates: Requirements 16.2, 16.3, 16.4, 16.5
 */

import { SubmissionStatus } from '../../types/database';
import type { Phase, SnapshotPage } from './fieldValidation';

// Re-export the shared `Phase` type so phase-gating callers can import it from
// this module alongside the gating functions, while the canonical definition
// stays in the lowest module (`fieldValidation.ts`).
export type { Phase } from './fieldValidation';

// ---------------------------------------------------------------------------
// Phase gate

/**
 * Whether Phase_2 is unlocked for an application. The gate is *exactly*
 * `applicationStatus === APPROVED` (Req 16.4) — `APPROVED` is the terminal
 * application-workflow state, and no other status (DRAFT, SUBMITTED, REJECTED)
 * unlocks Phase_2. This predicate is independent of any per-page approval axis
 * (Req 16.5): it reads only the application's own `applicationStatus`.
 *
 * Pure and total.
 */
export function isPhaseTwoUnlocked(applicationStatus: SubmissionStatus): boolean {
  return applicationStatus === SubmissionStatus.APPROVED;
}

// ---------------------------------------------------------------------------
// Available pages

/**
 * The Form_Pages available to an Applicant given their application status: every
 * Phase_1 page is always available (Req 16.2), and every Phase_2 page is
 * available iff Phase_2 is unlocked (Req 16.3) — i.e. iff
 * `applicationStatus === APPROVED` (Req 16.4).
 *
 * Page order is preserved. This function decides phase availability only; it
 * composes with per-division visibility (`formStructure.ts`) and audience
 * membership (`audienceVisibility.ts`) at the call site — a page must be
 * phase-available AND effectively visible to be rendered.
 *
 * Pure and total; the input `pages` array is never mutated.
 */
export function availablePages(
  pages: SnapshotPage[],
  applicationStatus: SubmissionStatus,
): SnapshotPage[] {
  const phaseTwoUnlocked = isPhaseTwoUnlocked(applicationStatus);
  return pages.filter((page) => isPageAvailable(page.phase, phaseTwoUnlocked));
}

/**
 * Whether a page of the given `phase` is available when Phase_2 is (un)locked.
 * Phase_1 pages are always available; Phase_2 pages require the unlock.
 */
function isPageAvailable(phase: Phase, phaseTwoUnlocked: boolean): boolean {
  return phase === 'PHASE_1' || phaseTwoUnlocked;
}
